import { ContentUtil } from '@csp/csp-common-content';
import { ZonedDateTimeDisplayMapper, ZonedDateTimeDisplayMapperOptions } from '@csp/csp-common-date-util';
import { BioSampleMapper } from '@csp/csp-common-hbs';
import {
  BioSample,
  BioSampleContainer,
  BioSampleContainerInformation,
  BioSampleContainerRefBase,
  BioSampleDeliveryLabReasonType,
  BioSampleFlowType,
  BioSampleKitType,
  BioSampleRef,
  BioSampleStatusValueType,
  BioSampleValueType,
  CollectionStatusValueType,
  CommentStatusValue,
  AnySampleStatusValueType,
  ProcessingStatusValueType,
  ShippingStatusValueType,
  VisitHbsKitConfig,
} from '@csp/csp-common-hbs-model';
import {
  Author,
  CustomStatusIn,
  CustomStatusValueIn,
  Maybe,
  Patient,
  UserId,
  ZonedDateTime,
} from '@csp/csp-common-model';
import { TextProps } from '@csp/csp-web-ui';
import { BookedPatientVisit } from '../../patient-visits/model/BookedPatientVisit';
import { BioSampleDisplay } from '../model/BioSampleDisplay';
import { BioSampleDisplayIn } from '../model/BioSampleDisplayIn';
import { BioSampleDisplayLabels } from '../model/BioSampleDisplayLabels';
import { BioSampleDisplayOptions } from '../model/BioSampleDisplayOptions';
import { BioSampleItem } from '../model/BioSampleItem';
import { CommentValueDisplay } from '../model/CommentValueDisplay';
import { ValueDisplayMapperOptions } from '../model/ValueDisplayMapperOptions';
import { BioSampleContentService } from '../service/BioSampleContentService';
import { SampleUtil } from '../util/SampleUtil';
import { BioSampleRefAndContainerInformation } from '../model/BioSampleRefAndContainerInformation';
import { AnyBioSampleItem } from '../model/AnyBioSampleItem';
import { EnrichSampleContainerFlow } from '../hbs-collection/model/EnrichSampleContainerFlow';
import { BioSampleAnalyteMapper } from './BioSampleAnalyteMapper';
import { ValueDisplayMapper } from './ValueDisplayMapper';
import { BioSampleSampleTypeMapper } from './BioSampleSampleTypeMapper';

const singleton: { statusEnum?: Record<string, string>; sampleDisplayLabels?: BioSampleDisplayLabels } = {};

const getStatusEnum = (): { enum: Record<string, string> } => {
  if (!singleton.statusEnum) {
    singleton.statusEnum = {
      ...BioSampleContentService.getCollectionTexts().collectionStatusValueType.enum,
      ...BioSampleContentService.getProcessingTexts().processingStatusValueType.enum,
      ...BioSampleContentService.getShippingTexts().shippingStatusValueType.enum,
    };
  }
  return {
    enum: singleton.statusEnum,
  };
};

const toBioSampleDisplayLabels = (): BioSampleDisplayLabels => {
  if (!singleton.sampleDisplayLabels) {
    const hbsTexts = BioSampleContentService.getCommonTexts();
    singleton.sampleDisplayLabels = {
      analyteTypeLabel: hbsTexts.header.header.analyteType.text,
      sampleTypeLabel: hbsTexts.header.header.sampleType.text,
      sampleTypeFullLabel: hbsTexts.header.header.sampleTypeFull.text,
      processSampleTypeLabel: hbsTexts.header.header.processedSampleType.text,
      processSampleTypeFullLabel: hbsTexts.header.header.processedSampleTypeFull.text,
      processStartDateTimeLabel: hbsTexts.header.header.processingStartTimestamp.text,
      processEndDateTimeLabel: hbsTexts.header.header.processingEndTimestamp.text,
      usedByLabel: hbsTexts.header.header.usedBy.text,
      usedDateTimeLabel: hbsTexts.header.header.usedDateTime.text,
      sampleStatusLabel: hbsTexts.header.header.sampleStatus.text,
      commentsLabel: hbsTexts.header.header.comments.text,
      shippingStatusTimestampLabel: hbsTexts.header.header.shippingTimestamp.text,
      shippingNumberLabel: hbsTexts.header.header.shippingNumber.text,
      createdLabel: hbsTexts.header.header.created.text,
      createdByLabel: hbsTexts.header.header.createdBy.text,
      patientLabel: hbsTexts.header.header.patient.text,
      patientVisitLabel: hbsTexts.header.header.patientVisit.text,
      sampleLabel: hbsTexts.header.header.sample.text,
      visitLabel: hbsTexts.header.header.visit.text,
      sampleCodeLabel: hbsTexts.header.header.sampleCode.text,
      kitCodeLabel: hbsTexts.header.header.kitCode.text,
      kitTypeLabel: hbsTexts.header.header.kitType.text,
      kitDescriptionLabel: hbsTexts.header.header.kitDescription.text,
      kitTypeAndDescriptionLabel: hbsTexts.header.header.kitTypeAndDescription.text,
      samplesLabel: hbsTexts.header.header.samples.text,
      kitLabel: hbsTexts.header.header.kit.text,
      containerNumberLabel: hbsTexts.header.header.containerNumber.text,
      sampleDescriptionLabel: hbsTexts.header.header.sampleDescription.text,
      sampleExpiryDateLabel: hbsTexts.header.header.sampleExpiryDate.text,
      storageComplianceStatusLabel: hbsTexts.header.header.storage.text,
      processComplianceStatusLabel: hbsTexts.header.header.processed.text,
      storageComplianceStatusShortLabel: hbsTexts.header.header.storageShort.text,
      processComplianceStatusShortLabel: hbsTexts.header.header.processedShort.text,
      storageComplianceTooltip: hbsTexts.header.header.storageComplianceStatus.text,
      processComplianceTooltip: hbsTexts.header.header.processComplianceStatus.text,
      processedByLabel: hbsTexts.header.header.processedBy.text,
      sampleStatusDateTimeLabel: hbsTexts.header.header.sampleStatusDateTime.text,
      shippedByLabel: hbsTexts.header.header.shippedBy.text,
      collectedByLabel: hbsTexts.header.header.collectedBy.text,
      collectedAtLabel: hbsTexts.header.header.collectedAt.text,
      collectDateTimeLabel: hbsTexts.header.header.collectDateTime.text,
      sampleRelationLabel: hbsTexts.header.header.sampleRelation.text,
      deliveryLabLabel: hbsTexts.header.header.deliveryLab.text,
      deliveryLabCommentLabel: hbsTexts.input.input.otherDeliveryLab.label,
    };
  }

  return singleton.sampleDisplayLabels;
};

const toDisplayKitDescription = (kitDescription: Maybe<string>, kitType: Maybe<BioSampleKitType>): string => {
  if (kitType && kitDescription) {
    return `${kitType} - ${kitDescription}`;
  } else if (kitType) {
    return kitType;
  } else if (kitDescription) {
    return kitDescription;
  } else {
    return '-';
  }
};

const toEvaluatedTextProps = (isMissing: boolean): Maybe<TextProps> => (isMissing ? { color: 'error' } : undefined);

const toSampleRelationValue = ({
  parent,
  aliquot,
}: {
  parent: Maybe<boolean>;
  aliquot: Maybe<boolean>;
}): Maybe<string> =>
  parent
    ? BioSampleContentService.getSampleDetailsTexts().drawer.enum.PARENT
    : aliquot
      ? BioSampleContentService.getSampleDetailsTexts().drawer.enum.ALIQUOT
      : undefined;

const toSampleStatusValue = (value: AnySampleStatusValueType): string =>
  ContentUtil.mapEnum(value, getStatusEnum(), BioSampleContentService.getCommonTexts().body.body.noData.text);

const getAuthor = (authors: Author[], userId: Maybe<UserId>): Maybe<string> =>
  authors.find(author => author.userId === userId)?.displayName;

const toCommentStatusValues = (
  authors: Author[],
  commentStatusValues: CommentStatusValue[],
): Maybe<CommentValueDisplay[]> =>
  commentStatusValues
    ? commentStatusValues.map(
        (value): CommentValueDisplay => ({
          author: getAuthor(authors, value.createdUserId) ?? '',
          comment: value.reasonText ?? '',
          timestamp: ZonedDateTimeDisplayMapper.toDisplayInTimestampTimezone(value.timestamp),
        }),
      )
    : undefined;

const toBioSampleDisplay = (
  bioSampleDisplayIn: BioSampleDisplayIn,
  { authors = [], bookedVisit, patient, bioSampleContainer }: BioSampleDisplayOptions,
): BioSampleDisplay => {
  const hbsTexts = BioSampleContentService.getCommonTexts();
  const optionalTimestampOptions: ZonedDateTimeDisplayMapperOptions = {
    hideMidnight: true,
  };
  const requiredTimestampOptions: ZonedDateTimeDisplayMapperOptions = {
    isRequired: true,
    hideMidnight: true,
    placeholder: hbsTexts.body.body.noData.text,
  };
  const optionalValueOptions: ValueDisplayMapperOptions = {};
  const requiredValueOptions: ValueDisplayMapperOptions = {
    isRequired: true,
    editablePlaceholder: hbsTexts.body.body.noData.text,
  };

  const isNotCollected = bioSampleDisplayIn.collectionStatus.value === CollectionStatusValueType.NOT_COLLECTED;
  const isAliquoted = bioSampleDisplayIn.collectionStatus.value === CollectionStatusValueType.ALIQUOTED;

  const createdAuthor = getAuthor(authors, bioSampleDisplayIn.createdBy);
  const createdDateTime = ZonedDateTimeDisplayMapper.toDisplayInLocalTimezone(
    bioSampleDisplayIn.createdAt,
    optionalTimestampOptions,
  );

  const collectionStatus = ValueDisplayMapper.from(
    ContentUtil.mapEnum(
      bioSampleDisplayIn.collectionStatus.value,
      BioSampleContentService.getCollectionTexts().collectionStatusValueType,
    ),
    optionalValueOptions,
  );
  const collectionStatusAuthor = getAuthor(authors, bioSampleDisplayIn.collectionStatus.createdUserId);
  const collectionComment = bioSampleDisplayIn.collectionStatus.reasonText ?? '-';
  const collectionStatusDateTime = ZonedDateTimeDisplayMapper.toDisplayInTimestampTimezone(
    bioSampleDisplayIn.collectionStatus.localTimestamp,
    requiredTimestampOptions,
  );

  const processStatus = ContentUtil.mapEnum(
    bioSampleDisplayIn.processingStatus.evaluatedStatusValue.value,
    BioSampleContentService.getProcessingTexts().processingStatusValueType,
  );
  const processStatusStartDateTime = ZonedDateTimeDisplayMapper.toDisplayInTimestampTimezone(
    bioSampleDisplayIn.processingStatus.startedAt,
    isAliquoted ? optionalTimestampOptions : requiredTimestampOptions,
  );
  const processStatusEndDateTime = ZonedDateTimeDisplayMapper.toDisplayInTimestampTimezone(
    bioSampleDisplayIn.processingStatus.endedAt,
    optionalTimestampOptions,
  );
  const processStatusAuthor = getAuthor(
    authors,
    bioSampleDisplayIn.processingStatus.evaluatedStatusValue.createdUserId,
  );

  const processComplianceStatus = ValueDisplayMapper.from(
    ContentUtil.mapEnum(bioSampleDisplayIn.processComplianceStatus.value, hbsTexts.bioComplianceStatusValueType),
  );

  const processComment = bioSampleDisplayIn.processingStatus.evaluatedStatusValue.reasonText ?? '-';

  const shippingNumberStr =
    bioSampleDisplayIn.shippingNumber.value !== BioSampleStatusValueType.UNKNOWN
      ? bioSampleDisplayIn.shippingNumber.value
      : undefined;
  const shippingNumber = ValueDisplayMapper.from(shippingNumberStr, requiredValueOptions);
  const shippingStatus = ContentUtil.mapEnum(
    bioSampleDisplayIn.shippingStatus.value,
    BioSampleContentService.getShippingTexts().shippingStatusValueType,
  );
  const shippingStatusAuthor = getAuthor(authors, bioSampleDisplayIn.shippingStatus.createdUserId);
  const shippingStatusDate = ZonedDateTimeDisplayMapper.toDisplayInTimestampTimezone(
    bioSampleDisplayIn.shippingStatus.localTimestamp,
    requiredTimestampOptions,
  );
  const shippingComment = bioSampleDisplayIn.shippingStatus.reasonText || '-';

  const sampleStatus = toSampleStatusValue(bioSampleDisplayIn.sampleStatus.value);
  const sampleStatusDateTime = ZonedDateTimeDisplayMapper.toDisplayInTimestampTimezone(
    bioSampleDisplayIn.sampleStatus.localTimestamp || bioSampleDisplayIn.sampleStatus.localTimestamp,
    optionalTimestampOptions,
  );
  const sampleRelationValue = toSampleRelationValue({
    parent: bioSampleDisplayIn.isAliquotParent,
    aliquot: Boolean(bioSampleDisplayIn.aliquotParentSampleId),
  });
  const storageComplianceStatusStr = ContentUtil.mapEnum(
    bioSampleDisplayIn.storageComplianceStatus.value,
    hbsTexts.bioComplianceStatusValueType,
  );
  const storageComplianceStatus = ValueDisplayMapper.from(
    storageComplianceStatusStr,
    isNotCollected ? optionalValueOptions : requiredValueOptions,
  );
  const sampleType = ValueDisplayMapper.from(
    BioSampleSampleTypeMapper.mapSampleTypeValueToText(bioSampleDisplayIn.sampleType.value),
    isNotCollected ? optionalValueOptions : requiredValueOptions,
  );
  const analyteType = ValueDisplayMapper.from(
    BioSampleAnalyteMapper.mapAnalyteValueToText(bioSampleDisplayIn.analyteType.value),
    isNotCollected ? optionalValueOptions : requiredValueOptions,
  );

  const processSampleType = ValueDisplayMapper.from(
    BioSampleSampleTypeMapper.mapProcessedSampleTypeValueToText(bioSampleDisplayIn.processedSampleType.value),
    isAliquoted && !isNotCollected ? requiredValueOptions : optionalValueOptions,
  );

  const commentStatusValues = bioSampleDisplayIn.commentStatusValues?.length
    ? toCommentStatusValues(authors, bioSampleDisplayIn.commentStatusValues)
    : undefined;
  const comments = bioSampleDisplayIn.comments.length ? bioSampleDisplayIn.comments.join('\n') : '-';

  const ecode = patient?.ecode || '-';
  const visitName = bookedVisit?.texts.visitName.value || '-';
  const kitDescription = toDisplayKitDescription(bioSampleDisplayIn.kitDescription, bioSampleDisplayIn.kitType);
  const containerDescription =
    bioSampleContainer?.containerDescription ||
    bioSampleDisplayIn.containerDescription ||
    hbsTexts.body.body.noContainerDescription.text;

  const otherDeliveryLab = bioSampleDisplayIn.deliveryLab?.reasonText === BioSampleDeliveryLabReasonType.OTHER;
  const deliveryLabValue =
    bioSampleDisplayIn.deliveryLab?.value === BioSampleStatusValueType.UNKNOWN
      ? undefined
      : bioSampleDisplayIn.deliveryLab?.value;

  const deliveryLab = ValueDisplayMapper.from(
    otherDeliveryLab ? hbsTexts.input.input.deliveryLabOtherOption.label : deliveryLabValue,
    {
      ...(isNotCollected ? optionalValueOptions : requiredValueOptions),
      secondaryValue: otherDeliveryLab ? deliveryLabValue : undefined,
    },
  );

  return {
    createdAuthor,
    createdDateTime,
    collectionStatus,
    collectionStatusDateTime,
    collectionStatusAuthor,
    collectionComment,

    processStatus,
    processStatusStartDateTime,
    processStatusEndDateTime,
    processStatusAuthor,
    processComplianceStatus,
    processComment,

    shippingStatus,
    shippingStatusDate,
    shippingStatusAuthor,
    shippingNumber,
    shippingComment,

    sampleStatus,
    sampleStatusDateTime,
    sampleRelationValue,
    storageComplianceStatus,
    sampleType,
    processSampleType,
    analyteType,
    commentStatusValues,
    comments,
    ecode,
    visitName,
    kitDescription,
    deliveryLab,
    containerDescription,

    ...toBioSampleDisplayLabels(),
  };
};

const toBioSampleItem = (
  sample: BioSample,
  bioSampleContainer: Maybe<BioSampleContainer>,
  aliquotParent: Maybe<BioSampleItem>,
  aliquotChildren: Maybe<BioSampleItem[]>,
  patient: Patient,
  bookedVisit: Maybe<BookedPatientVisit>,
  authors: Author[],
): BioSampleItem => ({
  ...sample,
  analyteType: {
    ...sample.analyteType,
    value: BioSampleAnalyteMapper.mapAnalyteValueToText(sample.analyteType.value) || BioSampleValueType.UNKNOWN,
  },
  bioSampleContainer,
  aliquotParent,
  aliquotChildren: aliquotChildren ?? [],
  patient,
  bookedVisit,
  isAliquotable: SampleUtil.evalIsAliquotable(sample, patient),
  isProcessed: sample.processingStatus.evaluatedStatusValue.value !== ProcessingStatusValueType.UNKNOWN,
  isShipped: sample.shippingStatus.value === ShippingStatusValueType.SHIPPED,
  isCollected: sample.collectionStatus.value === CollectionStatusValueType.COLLECTED,
  isNotCollected: sample.collectionStatus.value === CollectionStatusValueType.NOT_COLLECTED,
  isUsed: sample.shippingStatus.value === ShippingStatusValueType.USED,
  isEditable: true,
  display: toBioSampleDisplay(sample, {
    authors: authors || [],
    bookedVisit,
    patient,
    bioSampleContainer,
  }),
  unsupportedKitTypeMessage: SampleUtil.evalKitTypeAlertExistingSample(sample.kitType, bookedVisit?.hbsKitConfig),
});

const toUnknownStatusValueIn = (timestamp?: ZonedDateTime): CustomStatusValueIn => ({
  value: BioSampleStatusValueType.UNKNOWN,
  timestamp,
});

const toUnknownStatusIn = (type: string, timestamp?: ZonedDateTime): CustomStatusIn => ({
  type,
  ...toUnknownStatusValueIn(timestamp),
});

const toKitAndContainerIdentifiers = <
  T extends BioSampleRefAndContainerInformation,
  P extends Maybe<BioSampleContainerRefBase>,
>(
  sample: T,
  containerRef: P,
): Pick<BioSampleRef, 'kitCode' | 'containerNumber' | 'displayContainerNumber'> => {
  const shouldUpdate = !!containerRef?.containerNumber && !!containerRef.kitCode;
  return {
    ...BioSampleMapper.toBioSampleContainerNumber(
      shouldUpdate ? containerRef?.containerNumber : sample.containerNumber,
      sample.format,
    ),
    kitCode: shouldUpdate ? containerRef?.kitCode : sample.kitCode,
  };
};

const toContainerInformation = (container: BioSampleContainer): BioSampleContainerInformation => ({
  kitDescription: container.kitDescription,
  kitType: container.kitType,
  containerDescription: container.containerDescription,
  containerType: container.containerType,
  kitExpiryDate: container.kitExpirationDateStr,
});

const checkIfValueShallBePopulated = (currentValue: Maybe<string>, newValue: Maybe<string>): boolean =>
  (!currentValue || currentValue === BioSampleStatusValueType.UNKNOWN) && !!newValue;

const toStatusValueNoTimestamp = (value: Maybe<string>): Maybe<CustomStatusValueIn<string>> =>
  value
    ? {
        value,
        timestamp: undefined,
      }
    : undefined;

const toAnalyteTypeAndSampleTypeInformation = (
  flow: BioSampleFlowType,
  sample: AnyBioSampleItem,
  container: BioSampleContainer,
): Partial<Pick<BioSampleDisplayIn, 'sampleType' | 'analyteType' | 'processedSampleType'>> => ({
  analyteType: checkIfValueShallBePopulated(sample.analyteType?.value, container.containerAnalyteType)
    ? toStatusValueNoTimestamp(container.containerAnalyteType)
    : sample.analyteType,
  sampleType:
    flow === BioSampleFlowType.COLLECT &&
    checkIfValueShallBePopulated(sample.sampleType?.value, container.containerSampleType)
      ? toStatusValueNoTimestamp(container.containerSampleType)
      : sample.sampleType,
  processedSampleType:
    flow === BioSampleFlowType.ALIQUOT &&
    checkIfValueShallBePopulated(sample.processedSampleType?.value, container.containerSampleType)
      ? toStatusValueNoTimestamp(container.containerSampleType)
      : sample.processedSampleType,
});

const updateContainerInformation = <T extends AnyBioSampleItem>(
  flow: EnrichSampleContainerFlow,
  sample: T,
  container: Maybe<BioSampleContainer>,
  kitConfig: Maybe<VisitHbsKitConfig>,
): T => {
  const containerInfo = container ? toContainerInformation(container) : undefined;
  const analyteTypeAndSampleTypeInformation = container
    ? toAnalyteTypeAndSampleTypeInformation(flow, sample, container)
    : undefined;

  return {
    ...sample,
    ...containerInfo,
    ...analyteTypeAndSampleTypeInformation,
    bioSampleContainer: container,

    infoMessage: SampleUtil.evalKitTypeAlertAddedSample(containerInfo?.kitType, kitConfig),
  };
};

export const BioSampleItemMapper = {
  toBioSampleItem,
  toBioSampleDisplay,
  toBioSampleDisplayLabels,
  toSampleStatusValue,
  toEvaluatedTextProps,
  toUnknownStatusIn,
  toUnknownStatusValueIn,
  toKitAndContainerIdentifiers,
  toContainerInformation,
  updateContainerInformation,
};
